export type SessionHydrationStatus =
  | 'idle'
  | 'hydrating'
  | 'authenticated'
  | 'anonymous'
  | 'degraded';

export type SessionHydrationState<User> = {
  status: SessionHydrationStatus;
  user: User | null;
  error: unknown | null;
  attempt: number;
  recoverable: boolean;
};

export type SessionHydrationErrorKind = 'confirmed-anonymous' | 'transient';

export type SessionHydratorOptions<User> = {
  load: () => Promise<User>;
  getKnownUser: () => User | null;
  onStateChange?: (state: SessionHydrationState<User>) => void;
  classifyError?: (error: unknown) => SessionHydrationErrorKind;
  maxAttempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  attemptTimeoutMs?: number;
  random?: () => number;
  sleep?: (delayMs: number) => Promise<void>;
  timeout?: <Value>(promise: Promise<Value>, timeoutMs: number) => Promise<Value>;
};

export type SessionHydrator<User> = {
  hydrate: (options?: { force?: boolean }) => Promise<SessionHydrationState<User>>;
  retry: () => Promise<SessionHydrationState<User>>;
  getState: () => SessionHydrationState<User>;
};

export class SessionHydrationTimeoutError extends Error {
  constructor() {
    super('Session verification timed out');
    this.name = 'SessionHydrationTimeoutError';
  }
}

function defaultTimeout<Value>(promise: Promise<Value>, timeoutMs: number): Promise<Value> {
  return new Promise<Value>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SessionHydrationTimeoutError()), timeoutMs);
    void promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function errorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('status' in error)) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

export function classifySessionHydrationError(error: unknown): SessionHydrationErrorKind {
  const status = errorStatus(error);
  return status === 401 || status === 403 ? 'confirmed-anonymous' : 'transient';
}

export function sessionRetryDelayMs(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  random: () => number,
): number {
  const exponential = Math.min(maxDelayMs, baseDelayMs * 2 ** Math.max(0, attempt - 1));
  const jitter = 0.75 + Math.max(0, Math.min(1, random())) * 0.5;
  return Math.round(exponential * jitter);
}

export function createSessionHydrator<User>(
  options: SessionHydratorOptions<User>,
): SessionHydrator<User> {
  const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
  const baseDelayMs = Math.max(0, options.baseDelayMs ?? 250);
  const maxDelayMs = Math.max(baseDelayMs, options.maxDelayMs ?? 2_000);
  const attemptTimeoutMs = Math.max(1, options.attemptTimeoutMs ?? 10_000);
  const random = options.random ?? Math.random;
  const sleep =
    options.sleep ??
    ((delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs)));
  const classifyError = options.classifyError ?? classifySessionHydrationError;
  const timeout = options.timeout ?? defaultTimeout;

  let state: SessionHydrationState<User> = {
    status: 'idle',
    user: null,
    error: null,
    attempt: 0,
    recoverable: false,
  };
  let inFlight: Promise<SessionHydrationState<User>> | null = null;

  const publish = (next: SessionHydrationState<User>) => {
    state = next;
    options.onStateChange?.(state);
    return state;
  };

  const run = async () => {
    const knownUser = options.getKnownUser();
    publish({
      status: 'hydrating',
      user: knownUser,
      error: null,
      attempt: 0,
      recoverable: false,
    });

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const user = await timeout(options.load(), attemptTimeoutMs);
        return publish({
          status: 'authenticated',
          user,
          error: null,
          attempt,
          recoverable: false,
        });
      } catch (error) {
        if (classifyError(error) === 'confirmed-anonymous') {
          return publish({
            status: 'anonymous',
            user: null,
            error: null,
            attempt,
            recoverable: false,
          });
        }

        if (attempt < maxAttempts) {
          publish({
            status: 'hydrating',
            user: knownUser,
            error,
            attempt,
            recoverable: true,
          });
          await sleep(sessionRetryDelayMs(attempt, baseDelayMs, maxDelayMs, random));
          continue;
        }

        // No in-memory identity to preserve: prefer guest UX over blocking the app
        // when the session probe cannot reach the API (common during local startup).
        if (!knownUser) {
          return publish({
            status: 'anonymous',
            user: null,
            error,
            attempt,
            recoverable: true,
          });
        }

        return publish({
          status: 'degraded',
          user: knownUser,
          error,
          attempt,
          recoverable: true,
        });
      }
    }

    return state;
  };

  const hydrate = (options?: { force?: boolean }) => {
    if (inFlight && !options?.force) return inFlight;
    inFlight = run().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  return {
    hydrate,
    retry: () => hydrate({ force: true }),
    getState: () => state,
  };
}
